import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Columns3,
  Loader2,
  RefreshCcw,
  Search,
  Sigma,
  Table2,
} from "lucide-react";

import { Textarea } from "../../../ui/Textarea.tsx";
import {
  getSqlValidationContext,
  validateKnowledgeSql,
} from "../../../../apiWithKnowledge";
import {
  buildSqlReferenceIndex,
  getEditorColumnRef,
  getEditorTableRef,
} from "./sqlReferenceTransforms.js";

function formatDialectLabel(dialect) {
  const value = String(dialect || "").trim().toLowerCase();
  if (!value) return "SQL";
  if (value === "tsql") return "T-SQL";
  return value.toUpperCase();
}

function summarizeValidation(validation, validating) {
  if (validating) {
    return {
      tone: "muted",
      icon: Loader2,
      text: "Checking SQL...",
      iconClassName: "animate-spin",
    };
  }

  if (!validation) {
    return {
      tone: "muted",
      icon: Table2,
      text: "Live validation is ready.",
      iconClassName: "",
    };
  }

  if (validation.valid) {
    return {
      tone: "success",
      icon: CheckCircle2,
      text: "SQL looks valid for this dialect and schema.",
      iconClassName: "",
    };
  }

  const count = Array.isArray(validation.diagnostics) ? validation.diagnostics.length : 0;
  return {
    tone: "danger",
    icon: AlertTriangle,
    text: count > 0 ? `${count} validation issue${count > 1 ? "s" : ""} found.` : "SQL has validation issues.",
    iconClassName: "",
  };
}

function getToneClasses(tone) {
  switch (tone) {
    case "success":
      return "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400";
    case "danger":
      return "border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400";
    default:
      return "border-border bg-muted/40 text-muted-foreground";
  }
}

function normalizeIdentifier(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  return raw
    .split(".")
    .map((part) => {
      const trimmed = String(part || "").trim();
      if (
        trimmed.length >= 2 &&
        ((trimmed.startsWith("`") && trimmed.endsWith("`")) ||
          (trimmed.startsWith('"') && trimmed.endsWith('"')))
      ) {
        return trimmed.slice(1, -1).toLowerCase();
      }
      return trimmed.toLowerCase();
    })
    .filter(Boolean)
    .join(".");
}

function createInsertPayload(suggestion) {
  const insertText = suggestion.insertText || suggestion.value || "";
  const cursorOffset = Number(suggestion.cursorOffset || 0);
  return {
    text: insertText,
    cursorOffset,
  };
}

function getTableSearchTerms(tableEntry, referenceIndex) {
  const editorTableRef = getEditorTableRef(tableEntry, referenceIndex);
  return [
    tableEntry?.table_name,
    tableEntry?.logical_name,
    tableEntry?.physical_name,
    editorTableRef,
    tableEntry?.sql_ref,
    tableEntry?.required_table_ref,
    tableEntry?.schema,
    tableEntry?.owner,
    tableEntry?.project,
    tableEntry?.dataset,
  ]
    .filter(Boolean)
    .map(normalizeIdentifier)
    .join(" ");
}

function getColumnSearchTerms(tableEntry, column, referenceIndex) {
  return [
    tableEntry?.table_name,
    tableEntry?.logical_name,
    getEditorTableRef(tableEntry, referenceIndex),
    column?.name,
    column?.physical_name,
    column?.sql_ref,
    getEditorColumnRef(tableEntry, column, referenceIndex),
    column?.qualified_sql_ref,
    column?.db_dtype,
    column?.description,
    ...(column?.synonyms || []),
  ]
    .filter(Boolean)
    .map((item) => {
      const raw = String(item);
      const normalized = normalizeIdentifier(raw);
      return normalized ? `${raw.toLowerCase()} ${normalized}` : raw.toLowerCase();
    })
    .join(" ");
}

function buildSuggestions(schema, referenceIndex, dialectSuggestions, token) {
  const normalized = normalizeIdentifier(token);
  if (!normalized) return [];

  const hasQualifier = normalized.includes(".");
  const [tablePrefix, columnPrefix = ""] = normalized.split(".", 2);
  const suggestions = [];

  (dialectSuggestions || []).forEach((item) => {
    const label = String(item.label || "").trim();
    const insertText = String(item.insert_text || item.insertText || label).trim();
    const haystack = `${label} ${insertText}`.toLowerCase();
    if (!hasQualifier && haystack.includes(normalized)) {
      suggestions.push({
        key: `dialect-${label}`,
        label,
        insertText,
        cursorOffset: insertText.endsWith("()") ? 1 : 0,
        type: item.kind === "keyword" ? "keyword" : "function",
        meta: item.kind || "function",
        rank: item.kind === "keyword" ? 3 : 0,
      });
    }
  });

  Object.values(schema || {}).forEach((tableEntry) => {
    const tableRef = getEditorTableRef(tableEntry, referenceIndex);
    const columns = Object.values(tableEntry.columns || {});
    const tableTerms = getTableSearchTerms(tableEntry, referenceIndex);

    if (!hasQualifier) {
      if (tableTerms.includes(normalized)) {
        suggestions.push({
          key: `table-${tableRef}`,
          label: tableRef,
          insertText: tableRef,
          type: "table",
          meta: "table",
          rank: 1,
        });
      }

      columns.forEach((column) => {
        const columnName = String(column.name || column.physical_name || column.sql_ref || "").trim();
        const qualifiedRef = getEditorColumnRef(tableEntry, column, referenceIndex);
        const haystack = getColumnSearchTerms(tableEntry, column, referenceIndex);
        if (haystack.includes(normalized) || normalizeIdentifier(columnName).includes(normalized)) {
          suggestions.push({
            key: `column-${tableRef}-${columnName}`,
            label: qualifiedRef,
            insertText: qualifiedRef,
            type: "column",
            meta: column.db_dtype || "column",
            rank: 2,
          });
        }
      });
      return;
    }

    if (!tableTerms.includes(tablePrefix)) {
      return;
    }

    if (!columnPrefix) {
      suggestions.push({
        key: `table-${tableRef}`,
        label: tableRef,
        insertText: tableRef,
        type: "table",
        meta: "table",
        rank: 1,
      });
      columns.forEach((column) => {
        const columnName = String(column.name || column.physical_name || column.sql_ref || "").trim();
        const qualifiedRef = getEditorColumnRef(tableEntry, column, referenceIndex);
        suggestions.push({
          key: `column-${tableRef}-${columnName}`,
          label: qualifiedRef,
          insertText: qualifiedRef,
          type: "column",
          meta: column.db_dtype || "column",
          rank: 2,
        });
      });
      return;
    }

    columns.forEach((column) => {
      const columnName = String(column.name || column.physical_name || column.sql_ref || "").trim();
      const qualifiedRef = getEditorColumnRef(tableEntry, column, referenceIndex);
      const columnHaystack = getColumnSearchTerms(tableEntry, column, referenceIndex);
      if (columnHaystack.includes(columnPrefix)) {
        suggestions.push({
          key: `column-${tableRef}-${columnName}`,
          label: qualifiedRef,
          insertText: qualifiedRef,
          type: "column",
          meta: column.db_dtype || "column",
          rank: 2,
        });
      }
    });
  });

  return suggestions
    .sort((a, b) => {
      const rankDiff = Number(a.rank || 99) - Number(b.rank || 99);
      if (rankDiff !== 0) return rankDiff;
      return String(a.label || "").localeCompare(String(b.label || ""));
    })
    .slice(0, 8);
}

function getActiveToken(value, cursor) {
  const safeCursor = Number.isFinite(cursor) ? cursor : String(value || "").length;
  const beforeCursor = String(value || "").slice(0, safeCursor);
  const match = beforeCursor.match(/([A-Za-z_][A-Za-z0-9_.]*)$/);

  if (!match) {
    return null;
  }

  const token = match[1];
  return {
    token,
    start: safeCursor - token.length,
    end: safeCursor,
  };
}

function normalizeErrorMessage(item) {
  const message = String(item?.message || "").trim();
  const expectedRef = item?.meta?.expected_ref;
  if (!expectedRef || message.includes(expectedRef)) {
    return message;
  }
  return `${message} Use ${expectedRef}.`;
}

function measureCaretPosition(textarea, value, cursorPosition) {
  if (!textarea || typeof window === "undefined" || typeof document === "undefined") {
    return { top: 16, left: 16 };
  }

  const computed = window.getComputedStyle(textarea);
  const mirror = document.createElement("div");
  const span = document.createElement("span");
  const textBeforeCaret = String(value || "").slice(0, cursorPosition);
  const textAfterCaret = String(value || "").slice(cursorPosition) || ".";

  const mirrorStyles = [
    "boxSizing",
    "width",
    "height",
    "overflowX",
    "overflowY",
    "borderTopWidth",
    "borderRightWidth",
    "borderBottomWidth",
    "borderLeftWidth",
    "paddingTop",
    "paddingRight",
    "paddingBottom",
    "paddingLeft",
    "fontStyle",
    "fontVariant",
    "fontWeight",
    "fontStretch",
    "fontSize",
    "fontSizeAdjust",
    "lineHeight",
    "fontFamily",
    "letterSpacing",
    "textTransform",
    "textIndent",
    "whiteSpace",
    "wordBreak",
    "overflowWrap",
  ];

  mirror.style.position = "absolute";
  mirror.style.visibility = "hidden";
  mirror.style.top = "0";
  mirror.style.left = "-9999px";
  mirror.style.whiteSpace = "pre-wrap";
  mirror.style.wordBreak = "break-word";
  mirror.style.overflowWrap = "anywhere";

  mirrorStyles.forEach((prop) => {
    mirror.style[prop] = computed[prop];
  });

  mirror.textContent = textBeforeCaret;
  span.textContent = textAfterCaret[0] || ".";
  mirror.appendChild(span);
  document.body.appendChild(mirror);

  const mirrorRect = mirror.getBoundingClientRect();
  const spanRect = span.getBoundingClientRect();
  const paddingLeft = Number.parseFloat(computed.paddingLeft || "0") || 0;

  const top = Math.max(12, spanRect.top - mirrorRect.top - textarea.scrollTop + 24);
  const left = Math.max(12, spanRect.left - mirrorRect.left - textarea.scrollLeft + paddingLeft);

  document.body.removeChild(mirror);
  return { top, left: Math.max(left, paddingLeft) };
}

export default function SqlValidationEditor({
  sourceDbId,
  mode,
  expressionType,
  value,
  onValueChange,
  placeholder,
  rows = 5,
  minHeightClassName = "min-h-[140px]",
  requiredMessage = "",
}) {
  const [context, setContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextError, setContextError] = useState("");
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const [referenceSearch, setReferenceSearch] = useState("");
  const [cursorPosition, setCursorPosition] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const [suggestionAnchor, setSuggestionAnchor] = useState({ top: 16, left: 16 });
  const [schemaOpen, setSchemaOpen] = useState(false);
  const textareaRef = useRef(null);
  const schemaPopoverRef = useRef(null);
  const referenceIndex = useMemo(() => buildSqlReferenceIndex(context?.schema || {}), [context?.schema]);

  const runValidation = () => {
    if (!value.trim() || !context?.applicable) return Promise.resolve();
    setValidating(true);
    return validateKnowledgeSql({
      source_db_id: sourceDbId,
      sql: value,
      mode,
      expression_type: expressionType,
    })
      .then(({ data }) => setValidation(data || null))
      .catch(() =>
        setValidation({
          valid: false,
          diagnostics: [
            {
              code: "VALIDATION_UNAVAILABLE",
              message: "Could not validate SQL right now.",
              severity: "error",
            },
          ],
        }),
      )
      .finally(() => setValidating(false));
  };

  useEffect(() => {
    let cancelled = false;
    if (!sourceDbId) {
      setContext(null);
      setValidation(null);
      return undefined;
    }

    setContextLoading(true);
    setContextError("");

    getSqlValidationContext(sourceDbId)
      .then(({ data }) => {
        if (cancelled) return;
        setContext(data || null);
      })
      .catch(() => {
        if (cancelled) return;
        setContext(null);
        setContextError("Could not load SQL schema context.");
      })
      .finally(() => {
        if (!cancelled) setContextLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [sourceDbId]);

  useEffect(() => {
    let cancelled = false;
    if (!value.trim() || !context?.applicable) {
      setValidation(null);
      setValidating(false);
      return undefined;
    }

    setValidating(true);
    const timer = window.setTimeout(() => {
      validateKnowledgeSql({
        source_db_id: sourceDbId,
        sql: value,
        mode,
        expression_type: expressionType,
      })
        .then(({ data }) => {
          if (!cancelled) setValidation(data || null);
        })
        .catch(() => {
          if (!cancelled) {
            setValidation({
              valid: false,
              diagnostics: [
                {
                  code: "VALIDATION_UNAVAILABLE",
                  message: "Could not validate SQL right now.",
                  severity: "error",
                },
              ],
            });
          }
        })
        .finally(() => {
          if (!cancelled) setValidating(false);
        });
    }, 500);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [context?.applicable, context?.schema, expressionType, mode, sourceDbId, value]);

  const validationSummary = summarizeValidation(validation, validating);
  const hasExternalError = Boolean(requiredMessage);
  const hasValidationError = Boolean(validation && validation.valid === false);

  const filteredSchemaEntries = useMemo(() => {
    const search = referenceSearch.trim().toLowerCase();
    const schema = context?.schema || {};
    const entries = Object.values(schema);

    if (!search) return entries;

    return entries
      .map((tableEntry) => {
        const columns = Object.values(tableEntry.columns || {}).filter((col) => {
          const haystack = getColumnSearchTerms(tableEntry, col, referenceIndex);
          return haystack.includes(search);
        });

        if (getTableSearchTerms(tableEntry, referenceIndex).includes(search)) {
          return tableEntry;
        }

        if (!columns.length) return null;
        return {
          ...tableEntry,
          columns: Object.fromEntries(columns.map((col) => [col.name, col])),
        };
      })
      .filter(Boolean);
  }, [context?.schema, referenceSearch, referenceIndex]);

  const activeToken = useMemo(
    () => getActiveToken(value, cursorPosition),
    [cursorPosition, value],
  );

  const suggestions = useMemo(() => {
    if (!isFocused || !activeToken?.token) {
      return [];
    }
    return buildSuggestions(
      context?.schema || {},
      referenceIndex,
      context?.dialect_suggestions || [],
      activeToken.token,
    );
  }, [activeToken?.token, context?.dialect_suggestions, context?.schema, isFocused, referenceIndex]);

  useEffect(() => {
    setActiveSuggestionIndex(0);
  }, [activeToken?.token, suggestions.length]);

  useEffect(() => {
    if (!suggestions.length || !textareaRef.current) {
      return;
    }

    setSuggestionAnchor(measureCaretPosition(textareaRef.current, value, cursorPosition));
  }, [cursorPosition, suggestions.length, value]);

  useEffect(() => {
    if (!schemaOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!schemaPopoverRef.current?.contains(event.target)) {
        setSchemaOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [schemaOpen]);

  const syncCursorPosition = () => {
    const input = textareaRef.current;
    if (!input) return;
    setCursorPosition(input.selectionStart ?? String(value || "").length);
  };

  const handleInsert = (token, replaceToken = false, cursorOffset = 0) => {
    const input = textareaRef.current;
    if (!input) {
      onValueChange(`${value}${token}`);
      return;
    }

    const replacementRange = replaceToken && activeToken
      ? { start: activeToken.start, end: activeToken.end }
      : null;

    const start = replacementRange?.start ?? input.selectionStart ?? value.length;
    const end = input.selectionEnd ?? value.length;
    const finalEnd = replacementRange?.end ?? end;
    const nextValue = `${value.slice(0, start)}${token}${value.slice(finalEnd)}`;
    onValueChange(nextValue);

    window.requestAnimationFrame(() => {
      input.focus();
      const cursor = Math.max(start, start + token.length - cursorOffset);
      input.setSelectionRange(cursor, cursor);
      setCursorPosition(cursor);
    });
  };

  const handleSuggestionInsert = (suggestion) => {
    const payload = createInsertPayload(suggestion);
    handleInsert(payload.text, true, payload.cursorOffset);
  };

  const handleKeyDown = (event) => {
    if (!suggestions.length) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveSuggestionIndex((prev) => (prev + 1) % suggestions.length);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveSuggestionIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
      return;
    }

    if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault();
      handleSuggestionInsert(suggestions[activeSuggestionIndex]);
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setIsFocused(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center rounded-full border border-border bg-muted/40 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {formatDialectLabel(context?.dialect)}
        </span>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] ${getToneClasses(validationSummary.tone)}`}
        >
          <validationSummary.icon className={`h-3.5 w-3.5 ${validationSummary.iconClassName}`} />
          {validationSummary.text}
        </span>

        <div ref={schemaPopoverRef} className="relative ml-auto">
          <button
            type="button"
            onClick={() => setSchemaOpen((prev) => !prev)}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 text-[11px] font-medium text-foreground shadow-sm transition-colors hover:bg-muted"
          >
            Schema
            <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${schemaOpen ? "rotate-180" : ""}`} />
          </button>

          {schemaOpen && (
            <div className="absolute right-0 top-full z-30 mt-2 w-[320px] overflow-hidden rounded-xl border border-border bg-background shadow-xl ring-1 ring-black/5">
              <div className="border-b border-border px-3 py-2">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    value={referenceSearch}
                    onChange={(event) => setReferenceSearch(event.target.value)}
                    placeholder="Find table or column..."
                    className="h-8 w-full rounded-md border border-border bg-background pl-8 pr-3 text-xs text-foreground outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>

              <div className="max-h-72 space-y-2 overflow-y-auto p-3">
                {filteredSchemaEntries.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No matching tables or columns.</p>
                ) : (
                  filteredSchemaEntries.map((tableEntry) => {
                    const columns = Object.values(tableEntry.columns || {});
                    return (
                      <div key={tableEntry.table_name} className="rounded-lg border border-border bg-muted/20 p-2">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              handleInsert(getEditorTableRef(tableEntry, referenceIndex));
                              setSchemaOpen(false);
                            }}
                            className="inline-flex min-w-0 items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 text-xs font-semibold text-foreground transition-colors hover:bg-muted"
                          >
                            <Table2 className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                            <span className="truncate font-mono">{getEditorTableRef(tableEntry, referenceIndex)}</span>
                          </button>
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                            {columns.length} cols
                          </span>
                        </div>

                        <div className="space-y-1">
                          {columns.map((column) => (
                            <button
                              key={`${tableEntry.table_name}-${column.name}`}
                              type="button"
                              onClick={() => {
                                handleInsert(getEditorColumnRef(tableEntry, column, referenceIndex));
                                setSchemaOpen(false);
                              }}
                              className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-background"
                              title={column.description || column.db_dtype || column.name}
                            >
                              <span className="flex min-w-0 items-center gap-2">
                                <Columns3 className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                                <span className="truncate font-mono text-[12px] text-foreground">
                                  {getEditorColumnRef(tableEntry, column, referenceIndex)}
                                </span>
                              </span>
                              <span className="flex-shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground">
                                {column.db_dtype || "column"}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="relative">
        <Textarea
          ref={textareaRef}
          rows={rows}
          value={value}
          onChange={(event) => {
            onValueChange(event.target.value);
            setCursorPosition(event.target.selectionStart ?? event.target.value.length);
            setIsFocused(true);
          }}
          onClick={syncCursorPosition}
          onKeyUp={syncCursorPosition}
          onSelect={syncCursorPosition}
          onFocus={() => {
            setIsFocused(true);
            syncCursorPosition();
          }}
          onBlur={() => {
            window.setTimeout(() => setIsFocused(false), 120);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          error={hasValidationError || hasExternalError}
          className={`${minHeightClassName} resize-y font-mono text-[13px] leading-6 pb-12 pr-28`}
        />

        <button
          type="button"
          onClick={() => {
            void runValidation();
          }}
          disabled={!value.trim() || !context?.applicable || validating}
          className="absolute bottom-3 right-3 z-10 inline-flex items-center gap-1.5 rounded-md border border-border bg-background/95 px-2.5 py-1 text-[11px] font-medium text-foreground shadow-sm transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
        >
          <RefreshCcw className={`h-3 w-3 ${validating ? "animate-spin" : ""}`} />
          Check SQL
        </button>

        {suggestions.length > 0 && (
          <div
            className="absolute z-20 overflow-hidden rounded-xl border border-border bg-background shadow-xl ring-1 ring-black/5 backdrop-blur-sm"
            style={{
              top: suggestionAnchor.top,
              left: suggestionAnchor.left,
              width: "min(520px, calc(100% - 32px))",
              maxHeight: "220px",
            }}
          >
            <div className="border-b border-border bg-muted/40 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Recommendations
            </div>
            <div className="max-h-44 overflow-y-auto py-1">
              {suggestions.map((suggestion, index) => {
                const active = index === activeSuggestionIndex;
                return (
                  <button
                    key={suggestion.key}
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleSuggestionInsert(suggestion)}
                    className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors ${
                      active ? "bg-primary/10 text-foreground" : "hover:bg-muted/50 text-foreground"
                    }`}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      {suggestion.type === "table" ? (
                        <Table2 className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                      ) : suggestion.type === "column" ? (
                        <Columns3 className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                      ) : (
                        <Sigma className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                      )}
                      <span className="truncate font-mono text-[13px]">{suggestion.label}</span>
                    </span>
                    <span className="flex-shrink-0 text-[11px] uppercase tracking-wide text-muted-foreground">
                      {suggestion.meta}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {contextLoading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading schema context...
        </div>
      )}

      {!contextLoading && contextError && (
        <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-3 py-2 text-xs text-red-600 dark:text-red-400">
          {contextError}
        </div>
      )}

      {!contextLoading && context && context.applicable === false && (
        <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
          {context.reason || "SQL validation is not applicable for this database type."}
        </div>
      )}

      {hasExternalError && (
        <div className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{requiredMessage}</span>
        </div>
      )}

      {validation?.diagnostics?.length > 0 && (
        <div className="space-y-2">
          {validation.diagnostics.map((item, index) => (
            <div
              key={`${item.code || "diag"}-${index}`}
              className={`flex items-start gap-2 text-sm ${
                item.severity === "error"
                  ? "text-red-600 dark:text-red-400"
                  : "text-amber-700 dark:text-amber-300"
              }`}
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{normalizeErrorMessage(item)}</span>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}
